<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  variant?: 'primary'|'secondary'|'outline'|'outline-filled'|'text'|'icon';
  size?: 'small'|'medium'|'large';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  type?: 'button'|'submit'|'reset';
  tooltip?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'medium',
  disabled: false,
  loading: false,
  fullWidth: false,
  type: 'button',
  tooltip: ''
});

const emit = defineEmits(['click']);

const buttonClasses = computed(() => [
  'button',
  `button--${props.variant}`,
  `button--${props.size}`,
  {
    'button--disabled': props.disabled,
    'button--loading': props.loading,
    'button--full-width': props.fullWidth,
    'button--icon': props.variant === 'icon',
    'has-tooltip': !!props.tooltip
  }
]);

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event);
  }
};
</script>

<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    :type="type"
    @click="handleClick"
  >
    <span class="button__content">
      <span v-if="tooltip" class="button__tooltip">{{ tooltip }}</span>
      <slot />
    </span>
    <span v-if="loading" class="button__loader">
      <span class="button__loader-dot"></span>
      <span class="button__loader-dot"></span>
      <span class="button__loader-dot"></span>
    </span>
  </button>
</template>

<style scoped lang="scss">
.button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;

  &:focus-visible {
    outline-offset: 2px;
  }

  &--primary {
    color: white;
    border-radius: 8px;
  }

  &--outline {
    background-color: transparent;
    border: 2px solid #303030;
    color: #303030;
    border-radius: 50px;
    padding: 16px 32px;
    font-weight: bold;

    &:hover:not(:disabled) {
      background-color: rgba(37, 99, 235, 0.1);
    }
  }

  &--outline-filled {
    background-color: #303030;
    border: 2px solid #303030;
    color: white;
    border-radius: 50px;
    padding: 16px 32px;
    font-weight: bold;
  }

  &--icon {
    background-color: var(--black);
    border-radius: 50%;
    width: 48px;
    height: 48px;
    padding: 0;
    transition: all 0.3s ease;

    &.has-tooltip:hover {
      border-radius: 16px;
      width: auto;
      padding: 0 12px;
    }

  }

  &__content {
    display: flex;
    align-items: center;
  }

  &__tooltip {
    max-width: 0;
    opacity: 0;
    transition: all 0.3s ease;
    transform: translateX(10px);
    color: white;
  }

  &--icon.has-tooltip:hover &__tooltip {
    max-width: 200px;
    opacity: 1;
    transform: translateX(0);
    margin-right: 4px;
  }

  &--small {
    height: 24px;
    &.button--icon {
      width: 24px;
    }
  }

  &--medium {
    height: 32px;
    width: 100%;
    &.button--icon {
      width: 32px;
    }
  }

  &--large {
    height: 40px;
    &.button--icon {
      width: 40px;
    }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--loading .button__content {
    opacity: 0;
  }

  &__loader {
    position: absolute;
    display: flex;
    gap: 4px;
  }

  &__loader-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: currentColor;
    animation: button-loader 1.4s infinite both;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
}

@keyframes button-loader {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}
</style>
